import classNames from 'classnames';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputField, PasswordInputField } from '@/components/InputFields';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';

import HiddenIdentifierInput from './HiddenIdentifierInput';
import styles from './index.module.scss';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly onSubmit: (password: string, nickname?: string) => Promise<void>;
  readonly isNicknameEnabled?: boolean;
  readonly errorMessage?: string;
  readonly clearErrorMessage?: () => void;
};

type FieldState = {
  nickname: string;
  newPassword: string;
};

const Lite = ({
  className,
  autoFocus,
  onSubmit,
  errorMessage,
  clearErrorMessage,
  isNicknameEnabled = false,
}: Props) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FieldState>({
    reValidateMode: 'onBlur',
    defaultValues: { newPassword: '', nickname: '' },
  });

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage?.();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage?.();

      await handleSubmit(async (data) => {
        if (isNicknameEnabled) {
          await onSubmit(data.newPassword, data.nickname.trim() || undefined);
        } else {
          await onSubmit(data.newPassword);
        }
      })(event);
    },
    [clearErrorMessage, handleSubmit, isNicknameEnabled, onSubmit]
  );

  return (
    <form className={classNames(styles.form, className)} onSubmit={onSubmitHandler}>
      <HiddenIdentifierInput />
      {isNicknameEnabled && (
        <InputField
          className={styles.inputField}
          type="text"
          autoComplete="nickname"
          label={t('profile.nickname')}
          aria-label={t('input.label_with_optional', { label: t('profile.nickname') })}
          required={false}
          maxLength={128}
          isDanger={!!errors.nickname}
          aria-invalid={!!errors.nickname}
          errorMessage={errors.nickname?.message}
          {...register('nickname', {
            maxLength: {
              value: 128,
              message: t('error.invalid_min_max_length', { minLength: 0, maxLength: 128 }),
            },
          })}
        />
      )}
      <PasswordInputField
        className={styles.inputField}
        autoComplete="new-password"
        label={t('input.password')}
        autoFocus={autoFocus}
        isDanger={!!errors.newPassword}
        errorMessage={errors.newPassword?.message}
        aria-invalid={!!errors.newPassword}
        {...register('newPassword', {
          required: t('error.password_required'),
        })}
      />

      {errorMessage && <ErrorMessage className={styles.formErrors}>{errorMessage}</ErrorMessage>}

      <Button
        name="submit"
        title="action.save_password"
        htmlType="submit"
        isLoading={isSubmitting}
      />

      <input hidden type="submit" />
    </form>
  );
};

export default Lite;
